import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import { Data, HeadCell } from "./utils";
import { EnhancedTableToolbar } from "./EnhancedTableToolbar";
import { EnhancedTableHead } from "./EnhancedTableHead";
import IconButton from "@mui/material/IconButton/IconButton";
import { PaginationDescriptor } from "../../Models";
import { useState } from "react";

type BaseTableProps = {
  rows: Data[];
  headCells: readonly HeadCell[];
  selectionIcon: JSX.Element | null;
  selectionAction: ((selected: readonly string[]) => void) | null;
  rowActionIconWithLambda?: ((row: Data) => JSX.Element | null) | undefined;
  rowAction: ((id: string) => void) | null;
  rowActionIcon: JSX.Element | null;
  tabTitle: string;
  reload: (() => Promise<void>) | (() => void) | null;
};

type PaginatedTableProps = BaseTableProps & {
  paginated: true;
  onPagination: (descriptor: PaginationDescriptor) => void;
  currentPagination: PaginationDescriptor;
};

type TableComponentProps =
  | (BaseTableProps & {
      paginated?: false;
    })
  | PaginatedTableProps;

function BaseTable(
  props: BaseTableProps & {
    pageSize: number;
    count: number | -1;
    page: number;
    onPaginationChange: (paginationDescriptor: PaginationDescriptor) => void;
  }
) {
  const [selected, setSelected] = React.useState<readonly string[]>([]);

  const selectable = props.selectionAction !== null;
  const isSelected = (email: string) => selected.indexOf(email) !== -1;
  // const emptyRows =  page > 0 ? Math.max(0, (1 + page) * rowsPerPage - props.rows.length) : 0;

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = props.rows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const selectionAction = () => {
    if (props.selectionAction) {
      props.selectionAction(selected);
      setSelected([]);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <EnhancedTableToolbar
          deselect={() => setSelected([])}
          selectionIcon={props.selectionIcon}
          selectionAction={selectionAction}
          numSelected={selected.length}
          tabTitle={props.tabTitle}
          reload={props.reload}
        />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={"medium"}
          >
            <EnhancedTableHead
              selectable={selectable}
              numSelected={selected.length}
              onSelectAllClick={selectable ? handleSelectAllClick : () => {}}
              rowCount={props.rows.length}
              headCells={props.headCells}
            />
            <TableBody>
              {props.rows.map((row, index) => {
                const isItemSelected = selectable ? isSelected(row.id) : false;
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                    sx={{ cursor: "pointer", height: "48px" }}
                  >
                    <TableCell
                      onClick={(event) => {
                        if (selectable) {
                          handleClick(event, row.id);
                        }
                      }}
                      padding="checkbox"
                    >
                      {selectable && (
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          inputProps={{
                            "aria-labelledby": labelId,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      onClick={(event) => {
                        if (selectable) {
                          handleClick(event, row.id);
                        }
                      }}
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                    >
                      {row.firstElement}
                    </TableCell>
                    {row.elements?.map((element) => (
                      <TableCell
                        onClick={(event) => {
                          if (selectable) {
                            handleClick(event, row.id);
                          }
                        }}
                        align="right"
                      >
                        {element}
                      </TableCell>
                    ))}
                    {props.rowActionIcon && props.rowAction !== null ? (
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => props.rowAction!(row.firstElement)}
                        >
                          {props.rowActionIcon}
                        </IconButton>
                      </TableCell>
                    ) : props.rowActionIconWithLambda !== undefined &&
                      props.rowAction !== null ? (
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => props.rowAction!(row.firstElement)}
                        >
                          {props.rowActionIconWithLambda(row)}
                        </IconButton>
                      </TableCell>
                    ) : (
                      <></>
                    )}
                  </TableRow>
                );
              })}
              {/* {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )} */}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={props.count}
          rowsPerPage={props.pageSize}
          page={props.page}
          onPageChange={(_, page) =>
            props.onPaginationChange({ pageSize: props.pageSize, page })
          }
          onRowsPerPageChange={(e) =>
            props.onPaginationChange({
              page: props.page,
              pageSize: parseInt(e.target.value, 10),
            })
          }
        />
      </Paper>
    </Box>
  );
}

function TableComponent(props: TableComponentProps) {
  const [memoryPagination, setMemoryPagination] =
    useState<PaginationDescriptor>({ page: 0, pageSize: 5 });

  if (props.paginated) {
    return (
      <BaseTable
        {...props}
        page={props.currentPagination.page}
        pageSize={props.currentPagination.pageSize}
        count={-1}
        onPaginationChange={props.onPagination}
      />
    );
  }

  const { rows, ...p } = props;

  const startIdx = memoryPagination.page * memoryPagination.pageSize;
  const endIdx = startIdx + memoryPagination.pageSize;

  return (
    <BaseTable
      {...p}
      rows={rows.slice(startIdx, endIdx)}
      pageSize={memoryPagination.pageSize}
      page={memoryPagination.page}
      count={rows.length}
      onPaginationChange={setMemoryPagination}
    />
  );
}

export default TableComponent;
